import { Box, Flex, HStack, Icon, Text } from '@chakra-ui/react';
import type { ElementType, ReactNode } from 'react';
import { PARTBAY_BLUE, SETTINGS_BORDER_COLOR, SETTINGS_TABLE_GUTTER } from './constants';

export interface SettingsTableTab {
    id: string;
    label: string;
    icon?: ElementType;
    /** Shown in a small rounded grey badge next to the label. */
    count?: number;
    /**
     * Leading number in a filled circle, for tabs that are steps of a flow
     * ("1 Method / 2 Details") rather than views of one table.
     */
    number?: number;
    /** A step the user cannot reach yet; greyed out and not clickable. */
    isDisabled?: boolean;
}

export interface SettingsTableTabsProps {
    tabs: SettingsTableTab[];
    value: string;
    onChange: (id: string) => void;
    /** Right-aligned controls such as search or an Add button. */
    actions?: ReactNode;
    /**
     * Horizontal padding. Defaults to the full-bleed table gutter; pass 0 when
     * the tabs sit inside an already-padded column, above a SettingsSubTable.
     */
    px?: number | string;
    /** Gap below the row. Flows pass 0 so the steps sit flush on the content. */
    mb?: number | string;
    /**
     * Space above the labels. Tabs that sit directly under another bar (a flow's
     * steps under the breadcrumbs) pass this so the two rows do not crowd.
     */
    pt?: number | string;
}

/**
 * Underline tabs above a table, for switching between variants of the same
 * table (allowed / forbidden / flagged parts, for example). The active tab is
 * dark with a 2px dark underline sitting on the hairline that runs under the
 * whole row; inactive tabs are grey. Counts render as small grey badges.
 */
const SettingsTableTabs = ({
    tabs,
    value,
    onChange,
    actions,
    px = SETTINGS_TABLE_GUTTER,
    mb = 4,
    pt = 0,
}: SettingsTableTabsProps) => (
    <Flex
        justify="space-between"
        align="flex-end"
        gap={4}
        px={px}
        pt={pt}
        mb={mb}
        flexShrink={0}
        borderBottomWidth="1px"
        borderColor={SETTINGS_BORDER_COLOR}
    >
        <HStack spacing={5} minW={0} flexShrink={1}>
            {tabs.map((tab) => {
                const isActive = tab.id === value;
                const isDisabled = tab.isDisabled === true;
                return (
                    <Box
                        key={tab.id}
                        as="button"
                        type="button"
                        onClick={() => onChange(tab.id)}
                        disabled={isDisabled}
                        cursor={isDisabled ? 'default' : 'pointer'}
                        flexShrink={0}
                        pb={2.5}
                        // Overlap the row's hairline so the active underline
                        // replaces it rather than stacking above it.
                        mb="-1px"
                        borderBottomWidth="2px"
                        borderBottomColor={isActive ? 'gray.800' : 'transparent'}
                        color={isActive ? 'gray.800' : isDisabled ? 'gray.400' : 'gray.500'}
                        _hover={{
                            color: isActive ? 'gray.800' : isDisabled ? 'gray.400' : 'gray.700',
                        }}
                        transition="color 0.15s ease, border-color 0.15s ease"
                    >
                        <HStack spacing={2}>
                            {typeof tab.number === 'number' && (
                                <Flex
                                    boxSize="18px"
                                    borderRadius="full"
                                    align="center"
                                    justify="center"
                                    flexShrink={0}
                                    bg={isDisabled ? 'gray.200' : PARTBAY_BLUE}
                                    color={isDisabled ? 'gray.500' : 'white'}
                                    fontSize="10px"
                                    fontWeight="bold"
                                    lineHeight={1}
                                >
                                    {tab.number}
                                </Flex>
                            )}
                            {tab.icon && <Icon as={tab.icon} boxSize={3.5} />}
                            <Text
                                fontSize="sm"
                                fontWeight={isActive ? 'semibold' : 'medium'}
                                whiteSpace="nowrap"
                            >
                                {tab.label}
                            </Text>
                            {typeof tab.count === 'number' && (
                                <Box
                                    as="span"
                                    bg="gray.100"
                                    color="gray.600"
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    lineHeight={1}
                                    borderRadius="md"
                                    px={1.5}
                                    py={1}
                                >
                                    {tab.count}
                                </Box>
                            )}
                        </HStack>
                    </Box>
                );
            })}
        </HStack>
        {actions && (
            <HStack spacing={2} flexShrink={0} pb={2}>
                {actions}
            </HStack>
        )}
    </Flex>
);

export default SettingsTableTabs;
